import React from 'react';
import {Text, View} from '@react-pdf/renderer';
import styles, {
    getDepotAddress,
    getRadiusFromFilterSummary,
    getSummaryTitleStyle,
    getSummaryTitleText
} from '../../Foundations/Utils/PDFUtils';
import {IBaseSearchParam} from '../../Foundations/Types/ISearchParam';
import {ISearchFilterItem, ISearchFilterSet} from '../../Foundations/Types/ISearchFilter';
import {IGenericObject, IGenericStringArrayObject} from '../../Foundations/Types/IGenericObject';
import SearchFilterName from '../../Foundations/Enum/SearchFilterName';
import {getSupplierName} from '../../Foundations/Utils/SupplierUtils';
import {IResultSummary} from '../../Foundations/Types/IResultsData';
import {ActiveReportFilters} from '../../Foundations/Enum/ActiveReportFilters';
import {IDepotInfo, IDepotSummary} from '../../Foundations/Types/IDepot';
import {formatDate, parseDateString} from '../../Foundations/Utils/DateUtil';

interface IProps {
    searchParameters: IBaseSearchParam,
    availableFilters: ISearchFilterSet | null;
    activeFilters: ISearchFilterSet | null;
    filtersSummary: IGenericStringArrayObject | null;
    resultSummary: IResultSummary;
    searchRef: string;
    suppliersAtArea: string[];
    activeReportFilters: ActiveReportFilters[];
    depotsAtArea: IDepotInfo[];
    depotsWithNoResults: IDepotSummary[];
}

const ReportSummary: React.FC<IProps> = (props) => {
    let duration: string = '';
    const radius: string = getRadiusFromFilterSummary(props.filtersSummary, props.availableFilters);
    const carModels = Object.keys(props.resultSummary.models)
        .map((key: string) => props.resultSummary.models[key])
        .sort((a, b) => {
            return a.lowest.per_day_price - b.lowest.per_day_price
        });
    const carTypes: string[] = Object.keys(props.resultSummary.types);
    const hasCarModelsFilter: boolean = props.activeReportFilters.indexOf(ActiveReportFilters.CAR_MODEL) > -1;
    const hasSuppliersFilter: boolean = props.activeReportFilters.indexOf(ActiveReportFilters.SUPPLIERS) > -1;
    const hasDepotsFilter: boolean = props.activeReportFilters.indexOf(ActiveReportFilters.DEPOTS) > -1;
    let selectedSuppliers: string = '';
    let selectedCarModels: string[] =  [...carModels]
        .slice(0, 10)
        .map((data) => data.lowest.vehicle_data.name);
    let selectedDepots: ISearchFilterItem[] = [];

    const showLocation: boolean = props.activeReportFilters.indexOf(ActiveReportFilters.DEFAULT) > -1 ||
        (hasCarModelsFilter && !hasSuppliersFilter && !hasDepotsFilter);

    if (
        props.filtersSummary !== null &&
        props.availableFilters !== null &&
        props.filtersSummary.hasOwnProperty(props.availableFilters[SearchFilterName.DURATION].label)
    ) {
        duration = props.filtersSummary[props.availableFilters[SearchFilterName.DURATION].label]
            .map(item => item.replace('day(s)', ''))
            .join(',');
        duration += 'Day(s)';
    }

    const suppliersWithResults: IGenericObject[] = Object.keys(props.resultSummary.suppliersWithResults)
        .map((key: string) => {
            return {
                supplier_code: key,
                supplier_name: props.resultSummary.suppliersWithResults[key]
            }
        });

    const depotsWithResults: IGenericObject[] = Object.keys(props.resultSummary.depotsWithResults)
        .map((key: string) => {
            const split: string[] = key.split('_');

            return {
                depot_supplier_code: key,
                supplier_code: split[1],
                depot_code: split[0],
                depot_address: props.resultSummary.depotsWithResults[key]
            }
        });

    if (
        hasSuppliersFilter === true &&
        props.activeFilters !== null &&
        typeof props.activeFilters['supplier'] !== 'undefined' &&
        typeof props.activeFilters['supplier'].items !== 'undefined'
    ) {
        selectedSuppliers = props.activeFilters['supplier'].items
                                .filter((item: ISearchFilterItem) => item.selected === true)
                                .map((item: ISearchFilterItem) => item.label)
                                .join(', ');
    }

    if (
        hasCarModelsFilter === true &&
        props.activeFilters !== null &&
        typeof props.activeFilters['carModel'] !== 'undefined' &&
        typeof props.activeFilters['carModel'].items !== 'undefined'
    ) {
        selectedCarModels = props.activeFilters['carModel'].items
                                .filter((item: ISearchFilterItem) => item.selected === true)
                                .map((item: ISearchFilterItem) => item.value as string);
    }

    if (
        hasDepotsFilter === true &&
        props.activeFilters !== null &&
        typeof props.activeFilters['depot'] !== 'undefined' &&
        typeof props.activeFilters['depot'].items !== 'undefined'
    ) {
        selectedDepots = props.activeFilters['depot'].items.filter((item) => item.selected === true);
    }

    return (
        <View style={styles.reportSummary}>
            <View style={styles.reportSummaryDetails}>
                <View style={styles.reportSummaryLeftCol}>
                    {
                        hasSuppliersFilter === true &&
                        <View>
                            <View style={styles.inline}>
                                <Text style={getSummaryTitleStyle(props.activeReportFilters, ActiveReportFilters.SUPPLIERS)}>
                                    {getSummaryTitleText('SUPPLIER/S', props.activeReportFilters, ActiveReportFilters.SUPPLIERS)}
                                </Text>
                            </View>
                            <View>
                                <Text>{ selectedSuppliers }</Text>
                            </View>
                        </View>
                    }
                    {
                        selectedDepots.length > 0 &&
                        hasDepotsFilter === true &&
                        <View style={{marginTop: `${hasSuppliersFilter === true ? 12 : 0}`}}>
                            <View style={styles.inline}>
                                <Text style={getSummaryTitleStyle(props.activeReportFilters, ActiveReportFilters.DEPOTS)}>
                                    {getSummaryTitleText('DEPOT/S', props.activeReportFilters, ActiveReportFilters.DEPOTS)}
                                </Text>
                            </View>
                            {
                                selectedDepots
                                    .map((item: ISearchFilterItem, index: number) => (
                                        <View key={index} style={{ ...styles.row, flexWrap: 'wrap'}}>
                                            <Text style={{...styles.fontBold, fontSize: 12}}>{item.label}</Text>
                                            <Text style={{fontSize: 12}}>
                                                {getDepotAddress(props.depotsAtArea, item.value.toString())}
                                            </Text>
                                        </View>
                                    ))
                            }
                        </View>
                    }
                    {
                        showLocation === true &&
                        <View>
                            <Text style={{...styles.fontXs, ...styles.wFull}}>SEARCH LOCATION</Text>
                            <Text>{props.searchParameters.location}</Text>
                        </View>
                    }
                    <View style={{...styles.row, ...{marginTop: 12}}}>
                        <View style={hasDepotsFilter ? styles.wHalf : styles.w3}>
                            <View  style={styles.inline}>
                                <Text style={{ ...styles.inline, ...styles.summarySectionTitle}}>DATE</Text>
                            </View>
                            <Text>{formatDate(parseDateString(props.searchParameters.date), 'dd MMM yyyy')}</Text>
                        </View>
                        {
                            duration !== '' &&
                            (
                                <View style={hasDepotsFilter ? styles.wHalf : styles.w3}>
                                    <View style={styles.inline}>
                                        <Text style={getSummaryTitleStyle(props.activeReportFilters, ActiveReportFilters.DURATION)}>
                                            {getSummaryTitleText('DURATION', props.activeReportFilters, ActiveReportFilters.DURATION)}
                                        </Text>
                                    </View>
                                    <Text>{duration}</Text>
                                </View>
                            )
                        }
                        {
                            hasDepotsFilter === false &&
                            <View style={styles.w3}>
                                <View style={styles.inline}>
                                    <Text style={getSummaryTitleStyle(props.activeReportFilters, ActiveReportFilters.RADIUS)}>
                                        {getSummaryTitleText('RADIUS', props.activeReportFilters, ActiveReportFilters.RADIUS)}
                                    </Text>
                                </View>
                                <Text>{radius}</Text>
                            </View>
                        }
                    </View>
                    <View style={{...styles.row, ...{marginTop: 12}}}>
                        {
                            ((hasSuppliersFilter && suppliersWithResults.length > 1 ) || !hasSuppliersFilter) &&
                            <View style={{...styles.wHalf, ...styles.pr}}>
                                <View  style={styles.inline}>
                                    <Text style={{ ...styles.inline, ...styles.summarySectionTitle}}>
                                        { hasSuppliersFilter ? 'SUPPLIERS WITH RESULTS' : 'SUPPLIERS' }
                                    </Text>
                                </View>
                                <Text>{suppliersWithResults.length}</Text>
                            </View>
                        }
                        {
                            ((selectedDepots.length > 1 && hasDepotsFilter) || !hasDepotsFilter) &&
                            <View style={styles.wHalf}>
                                <View  style={styles.inline}>
                                    <Text style={{ ...styles.inline, ...styles.summarySectionTitle}}>DEPOTS WITH RESULTS</Text>
                                </View>
                                <Text>{depotsWithResults.length} out of {hasDepotsFilter ? selectedDepots.length : (props.depotsWithNoResults.length + depotsWithResults.length)}</Text>
                            </View>
                        }
                    </View>
                    <View style={{...styles.row, ...{marginTop: 12}}}>
                        <View style={{...styles.wHalf, ...styles.pr}}>
                            <View  style={styles.inline}>
                                <Text style={{ ...styles.inline, ...styles.summarySectionTitle}}>DRIVER AGE</Text>
                            </View>
                            <Text>25+</Text>
                        </View>
                        <View style={{...styles.wHalf, ...styles.pr}}>
                            <View  style={styles.inline}>
                                <Text style={{ ...styles.inline, ...styles.summarySectionTitle}}>VEHICLE TYPE</Text>
                            </View>
                            {
                               [...carTypes].splice(0, 3)
                                   .map((item, index) => (
                                       <Text key={index}>{item}</Text>
                                   ))
                            }
                            {
                                carTypes.length > 3 &&
                                <Text>
                                    (and {carTypes.length - 3} others)
                                </Text>
                            }
                        </View>
                    </View>
                    <View style={{...styles.row, ...{marginTop: 12}}}>
                        <View>
                            <View style={styles.inline}>
                                <Text style={getSummaryTitleStyle(props.activeReportFilters, ActiveReportFilters.CAR_MODEL)}>
                                    {getSummaryTitleText('VEHICLE MODEL/S', props.activeReportFilters, ActiveReportFilters.CAR_MODEL)}
                                </Text>
                            </View>
                            {
                                selectedCarModels.map((carModel: string, index: number) => (
                                    <Text style={{fontSize: 9}} key={index}>{carModel}</Text>
                                ))
                            }
                            {
                                !hasCarModelsFilter &&
                                carModels.length > 10 &&
                                <Text style={{fontSize: 9}}>
                                    (and {carModels.length - 10} others)
                                </Text>
                            }
                        </View>
                    </View>
                </View>
                {
                    props.resultSummary.lowestPerDay !== null &&
                    props.resultSummary.closest !== null &&
                    (
                        <View style={styles.reportSummaryRightCol}>
                            <Text style={{...styles.fontBold, ...styles.fontXs}}>LOWEST PER DAY (AUD)</Text>
                            <Text style={{...styles.fontXl, ...styles.fontBold, ...styles.my4}}>
                                ${props.resultSummary.lowestPerDay.per_day_price}
                            </Text>
                            <Text style={{...styles.fontXxs}}>
                                {props.resultSummary.lowestPerDay.duration} days
                            </Text>
                            <Text style={[{...styles.fontXxs, ...styles.fontBold}, {marginBottom: 12}]}>
                                {props.resultSummary.lowestPerDay.vehicle_data.name}
                            </Text>
                            <Text style={{...styles.fontBold, ...styles.fontXxs}}>
                                {props.resultSummary.lowestPerDay.supplier_name || getSupplierName(props.resultSummary.lowestPerDay.supplier_code)}
                            </Text>
                            <Text style={{...styles.fontXxs, ...styles.mb, ...styles.wHalf}}>
                                {getDepotAddress(props.depotsAtArea, `${props.resultSummary.lowestPerDay.depot_code}_${props.resultSummary.lowestPerDay.supplier_code}`)}
                            </Text>
                            <Text style={{...styles.fontBold, ...styles.fontXs, ...styles.mt}}>PER DAY CLOSEST DEPOT (AUD)</Text>
                            <Text style={{...styles.fontXl, ...styles.fontBold, ...styles.my4}}>
                                ${props.resultSummary.closest.per_day_price}
                            </Text>
                            <Text style={{...styles.fontXxs}}>
                                {props.resultSummary.closest.duration} days
                            </Text>
                            <Text style={[{...styles.fontXxs, ...styles.fontBold}, {marginBottom: 12}]}>
                                {props.resultSummary.closest.vehicle_data.name}
                            </Text>
                            <Text style={{...styles.fontBold, ...styles.fontXxs}}>
                                {props.resultSummary.closest.supplier_name || getSupplierName(props.resultSummary.closest.supplier_code)}
                            </Text>
                            <Text style={{...styles.fontXxs, ...styles.mb, ...styles.wHalf}}>
                                {getDepotAddress(props.depotsAtArea, `${props.resultSummary.closest.depot_code}_${props.resultSummary.closest.supplier_code}`)}
                            </Text>
                            <Text style={{...styles.fontXs, ...styles.textItalic}}>
                                Per Day price for rental duration selected
                            </Text>
                        </View>
                    )
                }
            </View>
        </View>
    )
}

export default ReportSummary;
